import { PICK_PAGE_TYPE_BY_PATH_SEGMENT } from '../constants';
import { IPickPageType } from '../types';

const TRAILING_SLASH_PATTERN = /\/+$/;

// Neither a query string nor an anchor a shared URL carries is part of the path.
const PATH_END_PATTERN = /[?#]/;

/** What an input asks for: a page type, a term to match, or — pasted in full — both. */
export interface IPickPageSearchIntent {
  /** The page type the input's path names, when it names one. */
  type?: IPickPageType;
  /** The term left to match, empty when the input names a type but no page of its own. */
  subject: string;
}

// A pasted URL can arrive percent-encoded, and decodeURIComponent throws on a stray `%`.
const decodeSearchValue = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

// The last type segment wins, so `/collections/all/products/gift-card` reads as the product it ends
// at rather than the collection it browses through.
const toTypeSegmentIndex = (segments: string[]): number =>
  segments.reduce(
    (last, segment, index) =>
      PICK_PAGE_TYPE_BY_PATH_SEGMENT[segment.toLowerCase()] ? index : last,
    -1,
  );

// A blog post's own handle is the second segment after `/blogs/`, the first being the blog holding
// it — so `/blogs/news` names every post in a blog rather than one post called "news".
const toHandleSegment = ({ segments, type }: { segments: string[]; type: IPickPageType }): string =>
  (type === IPickPageType.BLOG_POST ? segments[1] : segments.at(-1)) ?? '';

/**
 * Reads any accepted input — a title, a bare keyword, `/pages/<handle>` or the full storefront URL —
 * as the page type and the term it asks for. A path with no page behind it, `/products/`, asks for
 * that type alone.
 */
export const toPickPageSearchIntent = (searchValue: string): IPickPageSearchIntent => {
  const decoded = decodeSearchValue(searchValue.trim()).replace(TRAILING_SLASH_PATTERN, '');
  if (!decoded.includes('/')) return { subject: decoded };

  const segments = decoded.split(PATH_END_PATTERN)[0].split('/').filter(Boolean);
  const typeIndex = toTypeSegmentIndex(segments);
  if (typeIndex === -1) return { subject: segments.at(-1) ?? decoded };

  const type = PICK_PAGE_TYPE_BY_PATH_SEGMENT[segments[typeIndex].toLowerCase()];

  return { type, subject: toHandleSegment({ segments: segments.slice(typeIndex + 1), type }) };
};

/** The term worth matching, for the sources that read one without caring which type asked for it. */
export const toPickPageSearchSubject = (searchValue: string): string =>
  toPickPageSearchIntent(searchValue).subject;
